import { defineDocument } from '../../schema';
export const gameWtfIsHappeningDocument = defineDocument({
    id: 'game:wtf-is-happening',
    kind: 'game',
    slug: 'wtf-is-happening',
    route: '/discover/games/wtf-is-happening',
    seo: {
        title: 'Discover WTF Is Happening',
        description: 'A headline-writing game where you create news from acronyms. Vote on the best headlines and earn points to become the top reporter at the Airhead Times.',
        imageAsset: 'coverWTFIsHappening',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'WTF Is Happening',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Players craft newspaper headlines using random three-letter acronyms. Points are earned when headlines are submitted quickly or receive votes.',
            },
        ],
        imageAsset: 'coverWTFIsHappening',
        imageAlt: 'WTF Is Happening game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Players are freelance reporters at a struggling newspaper, The Airhead Times. Each round, you'll get a theme and a random three-letter acronym to pitch the perfect headline to Poppy, the Editor-in-Chief.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "You won't have much time to make journalistic magic before the deadline. The room votes on the headline with the most potential, and Poppy runs the story. Land the funniest, fastest headlines to earn points and rise through the ranks of the newsroom.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "It's newsroom nonsense meets acronym anarchy. Good luck making sense of WTF Is Happening.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '3-12 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~17 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'October 16, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '68eb1f40d63530c3bfd356e6',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'One player is selected to pick a newspaper section for the round.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'A random three-letter acronym is generated and displayed to all players.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players complete a headline using the three letters of the acronym and the theme of the newspaper section.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'All players vote on which headline has the most potential.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players are awarded points.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Poppy, the Editor-in-Chief, runs the winning newspaper headline complete with story and image.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat for four rounds with different themes and acronyms.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: '100 points for each vote your headline receives. If multiple players submitted the same headline, points are split evenly among them.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Speed bonus: 50 points per player distributed proportionally among all players based on how quickly they submit their headline. Faster submissions get a larger share of the pool.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The player with the highest score at the end of the game wins. If multiple players are tied for the highest score, the player with the highest speed score wins.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Headlines',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Newspaper section themes',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Newspaper stories',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Newspaper images',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: streamers, quick thinkers, wordplay enthusiasts',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/extra-extra-read-all-about-wtf-is-happening',
                            children: [
                                {
                                    type: 'text',
                                    text: 'WTF Is Happening blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '68eb1f40d63530c3bfd356e6',
                },
            ],
        },
    ],
});
//# sourceMappingURL=wtf-is-happening.js.map